import {
  EnquiryHandle,
  IServerSideGetRowsParams,
  ServerSideCriteria,
  combineFunctions,
  useServerEnquiry,
} from '@access-erp/common';
import { useRef, useState } from 'react';
import { Guid } from 'guid-typescript';
import { CustomDetailsProps } from '@access-erp/ui-components';

import { useDetailColumns } from './columns/useDetailColumns';
import { useHeaderColumns } from './columns/useHeaderColumns';
import useGetCustomers, { CustomerOutputWithId } from './hooks/useGetCustomers';
import { useGetInitialFiltersSet } from '@/components/common/EnquiriesScreen/hooks/useGetInitialFiltersSet';
import { GetCustomersVariables } from '@/graphql/contracts/_generated_/getCustomers.query';
import { replaceAdvancedValueType } from '@/components/common/EnquiriesScreen/utils/advancedFilterHelpers';
import { getSavedSearch } from '@/components/common/EnquiriesScreen/utils/savedSearch';
import { Forms } from '@/graphql/contracts/_generated_/contracts';
import { CONTRACTS_PER_PAGE } from '@/config/common.config';
import { ContractsEnquiriesScreen } from '@/components/common/EnquiriesScreen/providers/ContractsEnquiriesScreen';
import useGetDetails from './hooks/useGetDetails';
import useCustomerNavigation from './hooks/useCustomerNavigation';
import { useRightPanel } from './hooks/useRightPanel';
import { EditParentCustomerModal } from './components/EditParentCustomerModal/EditParentCustomerModal';

const mapFieldName = (fieldName: string): string => {
  if (fieldName === 'CcEmailAddress') {
    return 'CCEmailAddress';
  }
  return fieldName;
};

const mapFieldNames = (value: any): any => {
  if (!value) return value;

  if (Array.isArray(value)) {
    return value.map(item => ({
      ...item,
      field: mapFieldName(item.field),
    }));
  }

  if (typeof value === 'string') {
    return mapFieldName(value);
  }

  return value;
};

const CustomersComponent = (): React.JSX.Element => {
  const [pageIsLoading, setPageIsLoading] = useState(true);
  const gridRef = useRef<EnquiryHandle<CustomerOutputWithId>>(null);
  const { headerColumns } = useHeaderColumns();
  const { detailColumns } = useDetailColumns();
  const { goToCustomersBySearch } = useCustomerNavigation();
  const { getCustomers } = useGetCustomers();
  const { getChildCustomers } = useGetDetails();
  const { getSavedFilterIfAny, savedFilters } = useGetInitialFiltersSet();
  const enquiry = useServerEnquiry<
  CustomerOutputWithId,
  GetCustomersVariables['filter'],
  CustomerOutputWithId
  >({
    gridRef,
    headerColumns: headerColumns as any,
    detailColumns: detailColumns as any,
    initialFilter: replaceAdvancedValueType(savedFilters),
  });

  const { tabsConfiguration, isVisibleParentModal, closeParentModal } = useRightPanel({
    selectedHeaderRows: enquiry.selectedHeaderRows,
  });

  const searchTextFromQuery = getSavedSearch?.()?.savedSearch;

  const getData = async (
    params: IServerSideGetRowsParams<CustomerOutputWithId>,
    serverSideCriteria: ServerSideCriteria,
  ) => {
    await getSavedFilterIfAny(Forms.Customers, params);
    const data = await getCustomers({
      after: serverSideCriteria.nextPageCursor,
      first: CONTRACTS_PER_PAGE,
      filter: {
        summary: enquiry.serverSide?.current?.search ?? searchTextFromQuery,
        ...(enquiry.serverSide?.current?.customFilters ?? {}),
        ...serverSideCriteria.groupingFilters,
      },
      sort: mapFieldNames(serverSideCriteria.userSetSorting),
      group: mapFieldNames(serverSideCriteria.groupByColumn),
    });
    setPageIsLoading(false);
    const searchIsCleared = enquiry.serverSide?.current?.search === '';
    if (enquiry.serverSide?.current?.search || searchIsCleared) {
      goToCustomersBySearch(enquiry.serverSide?.current?.search);
    }
    return {
      items:
        data?.items?.map((el) => ({
          ...el,
          id: el?.customerID ?? Guid.create().toString(),
          hasDetailLines: (el?.childCustomersCount ?? 0) > 0,
        })) ?? [],
      pageInfo: {
        hasNextPage: data?.pageInfo?.hasNextPage,
        hasPreviousPage: data?.pageInfo?.hasPreviousPage,
        endCursor: data?.pageInfo?.endCursor,
        startCursor: data?.pageInfo?.startCursor,
      },
    };
  };

  const refreshGrid = () => {
    gridRef.current?.deselectRows();
    gridRef.current?.refreshServerSideGrid({
      purge: true,
    });
  };

  const customDetailsProps: CustomDetailsProps<CustomerOutputWithId> = {
    getData: async (detailId: string | number, filtersRef?: React.MutableRefObject<any>) => {
      const customerID = String(detailId);
      const result = await getChildCustomers(customerID, filtersRef as any);
      return {
        data: result.data as CustomerOutputWithId[] | undefined,
        loading: result.loading,
      };
    },
    primaryHeaderKey: 'customerID',
    filtersRef: enquiry.filtersRef as CustomDetailsProps<CustomerOutputWithId>['filtersRef'],
    detailRowSelection: 'none',
  };

  enquiry.refreshSearch = combineFunctions(enquiry.refreshSearch);
  return (
    <>
      <ContractsEnquiriesScreen
        ref={gridRef}
        enquiry={enquiry}
        filterForm={Forms.Customers}
        formName={Forms.Customers}
        headerProps={{
          primaryTitle: 'Customers',
          secondaryTitle: 'List',
          searchPlaceholder: 'Start typing Account Name, Number or Postcode',
          showExportAll: false,
          showExportCurrent: false,
          search: searchTextFromQuery,
        }}
        detailProps={{
          exportFileName: 'Customers',
          customDetailsProps,
          rowGroupPanelShow: 'always',
          masterDetail: false,
          getData: getData as any,
          pageSize: CONTRACTS_PER_PAGE,
          suppressRowClickSelection: false,
          rowSelection: { mode: 'singleRow' },
          loading: pageIsLoading,
        }}
        rightPanelTabs={tabsConfiguration}
      />
      {isVisibleParentModal && enquiry.selectedHeaderRows[0]?.data && (
        <EditParentCustomerModal
          refreshGrid={refreshGrid}
          selectedRow={enquiry.selectedHeaderRows[0].data}
          onClose={closeParentModal}
          isVisible={isVisibleParentModal}
        />
      )}
    </>
  );
};

export default CustomersComponent;
